import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import AnimationRevealPage from "../../components/helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "../../components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { Toaster, toast } from "react-hot-toast";
import illustration from "../../images/img/signup-illustration.svg";
import logo from "../../images/logo/logo-kz.svg";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/user-plus.svg";
import InputMask from "react-input-mask";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../FireBaseConfig";
import Header from "../../components/headers/light";
import { useTranslation } from "react-i18next";
import { getFirebaseErrorMessage } from "../../utils/errorMessages";
import errorMonitoring from "../../utils/errorMonitoring";
import { InlineSpinner } from "../../components/LoadingSpinner";

const Container = styled(ContainerBase)`
    ${tw`min-h-screen bg-gray-100 text-gray-900 font-medium flex justify-center -m-8`}
`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow-lg sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-12 flex flex-col items-center`;
const Heading = tw.h1`text-3xl xl:text-4xl font-extrabold text-gray-800`;
const SubHeading = tw.h2`text-lg text-gray-600 mt-2`;
const FormContainer = tw.div`w-full flex flex-col items-center`;
const Form = tw.form`mx-auto w-full max-w-sm`;

const Input = styled.input`
    ${tw`w-full px-4 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mt-5`}
    transition: all 0.2s ease-in-out;
    &:hover { border-color: #0ABD19; }
`;
const MaskedInput = styled(InputMask)`
    ${tw`w-full px-4 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mt-5`}
    transition: all 0.2s ease-in-out;
    &:hover { border-color: #0ABD19; }
`;

const SubmitButton = styled.button`
    ${tw`w-full px-4 py-3 bg-green-1002 text-white font-bold rounded-lg shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition duration-300`}
    height: auto; display: flex; align-items: center; justify-content: center;
    margin-top: 1rem; box-sizing: border-box; font-size: 1rem; width: 109%;
`;

const IllustrationContainer = styled.div`
    ${tw`sm:rounded-r-lg flex-1 text-center hidden lg:flex justify-center bg-gradient-to-r from-green-100 to-gray-100`}
`;
const IllustrationImage = styled.div`
    ${({ imageSrc }) => `background-image: url("${imageSrc}");`}
    ${tw`m-12 xl:m-16 w-full max-w-lg bg-contain bg-center bg-no-repeat`}
`;

const StyledLink = styled(Link)`
    color: #0abd19; font-weight: bold;
    &:hover { color: #067d10; text-decoration: underline; }
`;
const Text = styled.p`${tw`text-center mt-8 text-lg text-gray-600`}`;
const HighlightedText = styled(StyledLink)`
    color: #0abd19; font-weight: bold; text-decoration: none;
    &:hover { color: #067d10; text-decoration: underline; }
`;

export default function RegistrationPage({
                                             logoLinkUrl = "/",
                                             illustrationImageSrc = illustration
                                         }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const phoneRef = useRef(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: localStorage.getItem("email") || "",
        password: "",
        confirmPassword: ""
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const validateForm = () => {
        const { firstName, lastName, phone, email, password, confirmPassword } = formData;
        if (!firstName.trim()) return t("auth.register.validation.firstName");
        if (!lastName.trim()) return t("auth.register.validation.lastName");
        if (!phone.trim() || phone.length < 18) return t("auth.register.validation.phoneInvalid");
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            return t("auth.register.validation.emailInvalid");
        if (!password) return t("auth.register.validation.passwordRequired");
        if (password.length < 6) return t("auth.register.validation.passwordLength");
        if (password !== confirmPassword) return t("auth.register.validation.passwordsMismatch");
        return null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errorMessage = validateForm();
        if (errorMessage) {
            toast.error(errorMessage);
            return;
        }
        setLoading(true);
        try {
            const auth = getAuth();
            const userCredential = await createUserWithEmailAndPassword(
                auth, formData.email, formData.password
            );
            const user = userCredential.user;
            const userDocRef = doc(db, "users", user.uid);
            await setDoc(userDocRef, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                email: formData.email,
                userId: Math.floor(Math.random() * 10000)
            });

            // Track успешной регистрации
            errorMonitoring.addBreadcrumb('User registered', { userId: user.uid });

            toast.success(t("auth.register.toasts.success"));
            navigate("/PersonalArea");
        } catch (error) {
            console.error('Registration error:', error);

            // Логируем ошибку
            errorMonitoring.captureException(error, {
                action: 'registration',
                email: formData.email
            });

            // Показываем понятное сообщение
            const errorMessage = getFirebaseErrorMessage(error.code);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <AnimationRevealPage>
                <Container>
                    <Content>
                        <MainContainer>
                            <LogoLink href={logoLinkUrl}>
                                <LogoImage src={logo} alt="Eparcel" />
                            </LogoLink>
                            <MainContent>
                                <Heading>{t("auth.register.title")}</Heading>
                                <SubHeading>{t("auth.register.subtitle")}</SubHeading>

                                <FormContainer>
                                    {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

                                    <Form onSubmit={handleSubmit}>
                                        <Input
                                            name="firstName"
                                            type="text"
                                            placeholder={t("auth.register.placeholders.firstName")}
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                        />
                                        <Input
                                            name="lastName"
                                            type="text"
                                            placeholder={t("auth.register.placeholders.lastName")}
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                        />
                                        <MaskedInput
                                            ref={phoneRef}
                                            mask="+7 (999) 999-99-99"
                                            name="phone"
                                            type="text"
                                            placeholder={t("auth.register.placeholders.phone")}
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                        <Input
                                            name="email"
                                            type="email"
                                            placeholder={t("auth.register.placeholders.email")}
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                        <Input
                                            name="password"
                                            type="password"
                                            placeholder={t("auth.register.placeholders.password")}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                        />
                                        <Input
                                            name="confirmPassword"
                                            type="password"
                                            placeholder={t("auth.register.placeholders.confirmPassword")}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                        />

                                        <SubmitButton type="submit" disabled={loading}>
                                            {loading ? <InlineSpinner /> : <SignUpIcon className="icon" />}
                                            <span className="text">
                        {loading ? t("auth.register.submitting") : t("auth.register.submit")}
                      </span>
                                        </SubmitButton>

                                        <Text>
                                            {t("auth.register.agreePrefix")}{" "}
                                            <HighlightedText to="/TermsOfService">
                                                {t("auth.register.links.terms")}
                                            </HighlightedText>{" "}
                                            {t("auth.register.and")}{" "}
                                            <HighlightedText to="/PrivacyPolicy">
                                                {t("auth.register.links.privacy")}
                                            </HighlightedText>.
                                        </Text>

                                        <Text>
                                            {t("auth.register.haveAccount")}{" "}
                                            <HighlightedText to="/login">{t("auth.register.loginLink")}</HighlightedText>
                                        </Text>
                                    </Form>
                                </FormContainer>
                            </MainContent>
                        </MainContainer>

                        <IllustrationContainer>
                            <IllustrationImage imageSrc={illustrationImageSrc} />
                        </IllustrationContainer>
                    </Content>

                    <Toaster position="top-right" reverseOrder={false} />
                </Container>
            </AnimationRevealPage>
        </>
    );
}
